import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const InsuranceList = () => {
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";
  const navigate = useNavigate();
  const [policyModal, setPolicyModal] = useState({
    isOpen: false,
    documentUrl: null,
    insuranceData: null,
  });

  // Calculate default date range: last 15 days to next 30 days
  const getDefaultDateRange = () => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - 15); // 15 days ago

    const toDate = new Date(today);
    toDate.setDate(today.getDate() + 30); // 30 days from now

    return {
      from_date: fromDate.toISOString().split("T")[0],
      to_date: toDate.toISOString().split("T")[0],
    };
  };

  const [insurances, setInsurances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredInsurances, setFilteredInsurances] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    insurance: null,
  });

  // Date filters
  const [dateFilters, setDateFilters] = useState(getDefaultDateRange());
  const [isDefaultRange, setIsDefaultRange] = useState(true);

  useEffect(() => {
    fetchInsurances();
  }, []);

  useEffect(() => {
    if (searchText.trim() === "") {
      setFilteredInsurances(insurances);
    } else {
      const filtered = insurances.filter(
        (insurance) =>
          (insurance.vehicle_reg_no || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (insurance.insurance_policy_number || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (insurance.customer_name || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (insurance.insurance_company_name || "")
            .toLowerCase()
            .includes(searchText.toLowerCase())
      );
      setFilteredInsurances(filtered);
    }
  }, [searchText, insurances]);

  const fetchInsurances = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("vehicle_grage_id", grage_id);
      formData.append("vehicle_id", "");
      formData.append("from_date", dateFilters.from_date);
      formData.append("to_date", dateFilters.to_date);
      formData.append("insurance_under_sgm", "");

      const response = await axios.post(
        `${server_url}insurance.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status === "1") {
        setInsurances(response.data.result || []);
        setFilteredInsurances(response.data.result || []);
      } else if (response.data.status === "3") {
        toast.error("Please login first");
        navigate("/login");
      } else if (response.data.status === "0") {
        // No data found for this date range
        setInsurances([]);
        setFilteredInsurances([]);
        // Don't show error toast for no data
        if (response.data.error !== "No data found") {
          toast.error(response.data.error || "Failed to fetch insurances");
        }
      } else {
        toast.error(response.data.error || "Failed to fetch insurances");
        setInsurances([]);
        setFilteredInsurances([]);
      }
    } catch (error) {
      console.error("Fetch insurances error:", error);
      toast.error("Unable to fetch insurances. Please try again.");
      setInsurances([]);
      setFilteredInsurances([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSharePolicy = () => {
    const { documentUrl, insuranceData } = policyModal;
    if (!insuranceData) return;

    // Create shareable message
    const message = `Insurance Policy Details:
📋 Policy No: ${insuranceData.insurance_policy_number || ""}
🚗 Vehicle: ${insuranceData.vehicle_reg_no || ""}
🏢 Company: ${insuranceData.insurance_company_name || ""}
📅 Validity: ${formatDate(insuranceData.insurance_start_date)} to ${formatDate(
      insuranceData.insurance_valid_till
    )}
💵 Amount: ₹${parseFloat(insuranceData.insurance_amount || 0).toLocaleString(
      "en-IN"
    )}
  
View document: ${documentUrl}`;

    // For WhatsApp
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");

    toast.success("Share options opened");
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    fetchInsurances();
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsDefaultRange(false);
  };

  const handleResetToDefault = () => {
    const defaultRange = getDefaultDateRange();
    setDateFilters(defaultRange);
    setIsDefaultRange(true);
  };

  const handleEditInsurance = (insurance) => {
    navigate("/edit-insurance", { state: { insurance } });
  };

  const handleAddInsurance = () => {
    navigate("/add-insurance");
  };

  const openDeleteModal = (insurance) => {
    setDeleteModal({ isOpen: true, insurance });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, insurance: null });
  };

  const confirmDeleteInsurance = async () => {
    const insurance = deleteModal.insurance;
    if (!insurance) return;

    setDeleteLoading(insurance.insurance_id);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("insurance_id", insurance.insurance_id);
      formData.append("insurance_grage_id", grage_id);

      const response = await axios.post(
        `${server_url}delete_insurance.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status === "1") {
        toast.success("Insurance deleted successfully!");
        setInsurances((prev) =>
          prev.filter((i) => i.insurance_id !== insurance.insurance_id)
        );
        setFilteredInsurances((prev) =>
          prev.filter((i) => i.insurance_id !== insurance.insurance_id)
        );
        closeDeleteModal();
      } else {
        toast.error(response.data.error || "Failed to delete insurance");
      }
    } catch (error) {
      console.error("Delete insurance error:", error);
      toast.error("Unable to delete insurance. Please try again.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const getStatusColor = (validTill) => {
    if (!validTill) return "#666";
    const today = new Date();
    const expiryDate = new Date(validTill);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "#dc2626"; // Expired - Red
    if (diffDays <= 30) return "#f59e0b"; // Expiring soon - Orange
    return "#10b981"; // Valid - Green
  };

  const getStatusText = (validTill) => {
    if (!validTill) return "";
    const today = new Date();
    const expiryDate = new Date(validTill);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "Expired";
    if (diffDays <= 30) return "Expiring Soon";
    return "Valid";
  };

  const handleViewPolicy = (documentUrl, insurance) => {
    if (documentUrl) {
      setPolicyModal({
        isOpen: true,
        documentUrl: documentUrl,
        insuranceData: insurance,
      });
    } else {
      toast.error("No policy document available");
    }
  };

  return (
    <div className="history-container">
      {/* Sticky Header Section */}
      <div
        className="sticky-filter"
        style={{ position: "fixed", top: "56px", width: "100%" }}
      >
        <div className="home-icon">
          <div className="section-home">
            <form onSubmit={handleSearch} className="search-form">
              <div className="input-group same-line">
                <input
                  type="date"
                  name="from_date"
                  value={dateFilters.from_date}
                  onChange={handleDateFilterChange}
                  className="search-input"
                  placeholder="From Date"
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  name="to_date"
                  value={dateFilters.to_date}
                  onChange={handleDateFilterChange}
                  className="search-input"
                  placeholder="To Date"
                />
                <button type="submit" className="search-btn">
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </form>

            {/* Search Bar Below Date Filters */}
            <div style={{ marginTop: "10px" }}>
              <div className="input-group same-line">
                <input
                  type="text"
                  placeholder="Search ..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className="search-input"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="content-area insuracelist">
        {/* Insurance List */}
        <div className="home-vehicles">
          {loading ? (
            <div className="loading-vehicles">
              <div className="loading-spinner"></div>
              <div>Loading insurance policies...</div>
            </div>
          ) : filteredInsurances.length > 0 ? (
            <div className="vehicles-list home-icon">
              {filteredInsurances.map((insurance) => (
                <div
                  key={insurance.insurance_id}
                  className="vehicle-card section-home"
                  style={{
                    borderLeft:
                      "4px solid " +
                      getStatusColor(insurance.insurance_valid_till),
                  }}
                >
                  <div className="vehicle-header">
                    <table style={{ width: "100%", textAlign: "left" }}>
                      <tbody>
                        {/* Vehicle and Policy Info */}
                        <tr>
                          <td
                            style={{
                              width: "70%",
                              fontWeight: "bold",
                              fontSize: "16px",
                            }}
                            onClick={() => handleEditInsurance(insurance)}
                          >
                            {insurance.vehicle_reg_no || ""}
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              width: "30%",
                              fontWeight: "bold",
                              color: "#0352a1",
                            }}
                          >
                            <button
                              onClick={() => openDeleteModal(insurance)}
                              disabled={
                                deleteLoading === insurance.insurance_id
                              }
                              style={{
                                flex: 1,
                                border: "none",
                                cursor: "pointer",
                                fontSize: "19px",
                                fontWeight: "500",
                                backgroundColor: "transparent",
                              }}
                            >
                              {deleteLoading === insurance.insurance_id ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-trash"
                                    style={{
                                      marginRight: "5px",
                                      color: "#DB4437",
                                    }}
                                  ></i>
                                </>
                              )}
                            </button>
                          </td>
                        </tr>

                        {/* Insurance Company */}
                        <tr>
                          <td
                            colSpan="2"
                            style={{
                              fontSize: "14px",
                              color: "#2c3e50",
                              padding: "4px 0",
                            }}
                            onClick={() => handleEditInsurance(insurance)}
                          >
                            {insurance.insurance_company_name || ""}
                            {insurance?.brand_name ||
                              insurance.brand_name ||
                              ""}
                            {insurance?.model_name || insurance.model_name
                              ? ` ${
                                  insurance?.model_name || insurance.model_name
                                }`
                              : ""}
                          </td>
                        </tr>

                        {/* Policy Number and Status */}
                        <tr>
                          <td
                            style={{
                              fontSize: "13px",
                              color: "#666",
                              padding: "2px 0",
                            }}
                            onClick={() => handleEditInsurance(insurance)}
                          >
                            <i
                              className="fas fa-file-alt"
                              style={{ marginRight: "8px" }}
                            ></i>
                            {insurance.insurance_policy_number || ""}
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              fontSize: "13px",
                              fontWeight: "500",
                              color: getStatusColor(
                                insurance.insurance_valid_till
                              ),
                            }}
                            onClick={() => handleEditInsurance(insurance)}
                          >
                            {getStatusText(insurance.insurance_valid_till)}
                          </td>
                        </tr>

                        {/* Customer Info */}
                        {insurance.customer_name && (
                          <tr>
                            <td
                              colSpan="2"
                              style={{
                                fontSize: "13px",
                                color: "#666",
                                padding: "2px 0",
                              }}
                              onClick={() => handleEditInsurance(insurance)}
                            >
                              <i
                                className="fas fa-user"
                                style={{ marginRight: "8px" }}
                              ></i>
                              {insurance.customer_name}
                              <a href={`tel:${insurance.customer_mobile}`}>
                                {" "}
                                <u>
                                  {insurance.customer_mobile &&
                                    ` (${insurance.customer_mobile})`}
                                </u>
                              </a>
                            </td>
                          </tr>
                        )}

                        {/* Validity Period */}
                        <tr>
                          <td
                            style={{
                              fontSize: "13px",
                              color: "#666",
                              padding: "2px 0",
                            }}
                            onClick={() => handleEditInsurance(insurance)}
                          >
                            <i
                              className="fas fa-calendar-alt"
                              style={{ marginRight: "8px" }}
                            ></i>
                            {formatDate(insurance.insurance_start_date)} -{" "}
                            {formatDate(insurance.insurance_valid_till)}
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#27ae60",
                              paddingTop: "8px",
                            }}
                            onClick={() => handleEditInsurance(insurance)}
                          >
                            ₹
                            {parseFloat(
                              insurance.insurance_amount || 0
                            ).toLocaleString("en-IN")}
                          </td>
                        </tr>

                        {/* Amount */}

                        {/* Policy Document Button */}
                        {insurance.insurance_policy_document && (
                          <tr>
                            <td colSpan="2" style={{ paddingTop: "12px" }}>
                              <button
                                onClick={() =>
                                  handleViewPolicy(
                                    insurance.insurance_policy_document,
                                    insurance
                                  )
                                }
                                style={{
                                  background: "#10b981",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "8px",
                                  padding: "8px 16px",
                                  fontSize: "13px",
                                  cursor: "pointer",
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "8px",
                                }}
                              >
                                <i className="fas fa-eye"></i>
                                View Policy Document
                              </button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-vehicles section-home">
              <div className="no-vehicles-icon">📄</div>
              <div className="no-vehicles-title">
                No Insurance Policies Found
              </div>
              <div className="no-vehicles-text">
                {searchText
                  ? "Try adjusting your search terms"
                  : `No insurance policies found for the selected date range (${formatDate(
                      dateFilters.from_date
                    )} to ${formatDate(dateFilters.to_date)})`}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Add Button */}
      <button
        className="floating-add-btn"
        onClick={handleAddInsurance}
        title="Add New Insurance"
      >
        <i className="fas fa-plus"></i>
      </button>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "350px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                margin: "0 auto 20px",
                backgroundColor: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-exclamation-triangle"
                style={{ fontSize: "24px", color: "#dc2626" }}
              ></i>
            </div>

            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Delete Insurance Policy
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Are you sure you want to delete insurance policy for{" "}
              <strong>
                {deleteModal.insurance?.vehicle_reg_name ||
                  deleteModal.insurance?.vehicle_reg_no}
              </strong>
              ? This action cannot be undone.
            </p>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={closeDeleteModal}
                disabled={deleteLoading !== null}
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "2px solid #e2e8f0",
                  background: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteInsurance}
                disabled={deleteLoading !== null}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {deleteLoading === deleteModal.insurance?.insurance_id ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ marginRight: "5px" }}
                    ></i>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Policy Document Modal */}
      {policyModal.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "10px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 10px",
                borderBottom: "1px solid #e2e8f0",
                background: "#0352a1",
                color: "white",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <i className="fas fa-file-pdf" style={{ fontSize: "20px" }}></i>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "600" }}>
                  Policy Document
                </h3>
              </div>
              <div
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                <button
                  onClick={handleSharePolicy}
                  style={{
                    background: "#25D366",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    fontSize: "13px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <i className="fab fa-whatsapp"></i>
                  Share
                </button>
                <button
                  onClick={() =>
                    setPolicyModal({
                      isOpen: false,
                      documentUrl: null,
                      insuranceData: null,
                    })
                  }
                  style={{
                    background: "transparent",
                    color: "white",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    padding: "0",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            {/* Document Preview */}
            <div
              style={{
                flex: 1,
                padding: "0px",
                overflow: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {policyModal.documentUrl ? (
                <img
                  src={policyModal.documentUrl}
                  style={{
                    width: "100%",
                    height: "70vh",
                    border: "none",
                    borderRadius: "8px",
                    objectFit:"contain"
                  }}
                  title="Policy Document"
                />
              ) : (
                <div style={{ textAlign: "center", color: "#666" }}>
                  <i
                    className="fas fa-file-alt"
                    style={{ fontSize: "48px", marginBottom: "16px" }}
                  ></i>
                  <p>Document not available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Add to existing styles */
        @media (max-width: 768px) {
          /* Add this for modal responsiveness */
          .policy-modal {
            padding: 10px;
          }

          .policy-modal iframe {
            height: 60vh;
          }

          .modal-header h3 {
            font-size: 14px;
          }

          .share-btn,
          .open-btn {
            font-size: 12px;
            padding: 6px 10px;
          }
        }
        .history-container {
          min-height: 100vh;
          background: white;
          padding: 0;
          position: relative;
        }

        .sticky-filter {
          position: sticky;
          top: 0;
          z-index: 100;
          background: white;
          padding: 8px 0;
          border-bottom: 1px solid #e2e8f0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .content-area {
          padding-top: 120px; /* Increased for two rows of filters */
          padding-bottom: 80px;
        }

        .home-icon .section-home {
          padding: 8px 12px 12px 12px;
        }
        .home-icon {
          padding-left: 0px;
          padding-right: 0px;
        }

        .input-group.same-line {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .search-input {
          flex: 1;
          padding: 10px 12px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 14px;
          background: #f8f9fa;
        }

        .date-separator {
          color: #718096;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
        }

        .search-btn {
          background: #0352a1;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 10px 12px;
          min-width: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .reset-btn {
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 10px 12px;
          min-width: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .default-indicator {
          margin-top: 8px;
          text-align: center;
        }

        .default-badge {
          background: #e3f2fd;
          color: #0352a1;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .default-badge i {
          font-size: 14px;
        }

        .home-vehicles {
          padding: 20px 10px;
        }

        .loading-vehicles {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .loading-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #0352a1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .vehicles-list {
          display: grid;
          gap: 15px;
        }

        .vehicle-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          border-left: 4px solid #0352a1;
          transition: all 0.3s ease;
        }

        .vehicle-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .vehicle-header {
          width: 100%;
        }

        .vehicle-header table {
          width: 100%;
          border-collapse: collapse;
        }

        .vehicle-header td {
          padding: 4px 0;
          vertical-align: top;
        }

        .no-vehicles {
          text-align: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 12px;
          margin: 20px 0;
        }

        .no-vehicles-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }

        .no-vehicles-title {
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 8px;
        }

        .no-vehicles-text {
          color: #666;
          margin-bottom: 20px;
          line-height: 1.4;
        }

        .reset-default-btn {
          background: #0352a1;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 10px 20px;
          font-size: 14px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin: 0 5px;
        }

        .reset-default-btn:hover {
          background: #023e7d;
        }

        .floating-add-btn {
          position: fixed;
          bottom: 80px;
          right: 20px;
          width: 60px;
          height: 60px;
          background: #0352a1;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(3, 82, 161, 0.3);
          z-index: 99;
          transition: all 0.3s ease;
          border: none;
        }

        .floating-add-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(3, 82, 161, 0.4);
        }

        .floating-add-btn:active {
          transform: scale(0.95);
        }

        @media (max-width: 768px) {
          .content-area {
            padding-top: 140px; /* More space for mobile */
          }

          .home-vehicles {
            padding: 15px 8px;
          }

          .vehicle-card {
            padding: 12px;
          }

          .vehicle-header td {
            font-size: 14px;
          }

          .sticky-filter {
            padding: 6px 0;
          }

          .input-group.same-line {
            flex-wrap: wrap;
          }

          .search-input {
            min-width: 120px;
          }

          .date-separator {
            display: none;
          }

          .floating-add-btn {
            bottom: 70px;
            right: 15px;
            width: 56px;
            height: 56px;
            font-size: 22px;
          }
        }

        @media (max-width: 480px) {
          .content-area {
            padding-top: 110px;
          }

          .sticky-filter {
            padding: 4px 0;
          }

          .input-group.same-line {
            gap: 6px;
          }

          .search-input {
            font-size: 13px;
            padding: 8px 10px;
          }

          .search-btn,
          .reset-btn {
            padding: 8px 10px;
            min-width: 36px;
          }

          .floating-add-btn {
            bottom: 60px;
            right: 12px;
            width: 52px;
            height: 52px;
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default InsuranceList;
